import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Topbar from "@/components/Topbar";
import { Badge } from "@/components/ui/badge";
import { Info } from "@phosphor-icons/react";
import { settingsApi } from "@/lib/api";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { settingsApi.get().then(setSettings); }, []);

  return (
    <AppLayout>
      <Topbar title="Settings" subtitle="Integrations and business configuration" />
      <div className="px-6 lg:px-10 py-8 max-w-3xl space-y-6">
        <div className="border border-border bg-card rounded-md p-6">
          <h3 className="font-display text-xl font-semibold">Business</h3>
          <p className="text-sm text-muted-foreground mt-1">Information used by the AI agent in generated scripts.</p>
          <div className="mt-5 flex items-center justify-between py-3 border-t border-border">
            <div className="text-sm text-muted-foreground">Business Name</div>
            <div className="font-mono text-sm">{settings?.business_name || "—"}</div>
          </div>
        </div>

        <div className="border border-border bg-card rounded-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-xl font-semibold">Twilio Integration</h3>
              <p className="text-sm text-muted-foreground mt-1">Required for live WhatsApp messages and voice calls.</p>
            </div>
            <Badge
              data-testid="settings-twilio-badge"
              className={settings?.twilio_configured ? "bg-success text-white" : "bg-muted text-muted-foreground border border-border"}
            >
              {settings?.twilio_configured ? "Connected" : "Simulation Mode"}
            </Badge>
          </div>
          <div className="mt-5 py-3 border-t border-border grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider font-mono">WhatsApp Sender</div>
              <div className="font-mono mt-1">{settings?.whatsapp_from || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider font-mono">Voice Number</div>
              <div className="font-mono mt-1">{settings?.voice_from || "—"}</div>
            </div>
          </div>

          <div className="mt-5 bg-muted/50 border border-border rounded-md p-4 flex gap-3">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" weight="duotone" />
            <div className="text-sm text-foreground/85">
              <div className="font-medium">To enable live sending:</div>
              <ol className="list-decimal ml-5 mt-2 space-y-1 text-foreground/75">
                <li>Create a Twilio account and get your <span className="font-mono">Account SID</span> and <span className="font-mono">Auth Token</span>.</li>
                <li>Buy a voice-capable number and set up the WhatsApp sender (or join the sandbox for testing).</li>
                <li>Add the keys to the backend <span className="font-mono">.env</span> file: <span className="font-mono">TWILIO_ACCOUNT_SID</span>, <span className="font-mono">TWILIO_AUTH_TOKEN</span>, <span className="font-mono">TWILIO_WHATSAPP_FROM</span>, <span className="font-mono">TWILIO_VOICE_FROM</span>.</li>
                <li>Restart the backend. The badge above will switch to <span className="font-semibold">Connected</span>.</li>
              </ol>
              <div className="mt-4 pt-3 border-t border-border">
                <div className="font-medium">Inbound WhatsApp replies (AI auto-classifies into paid / promise / dispute):</div>
                <p className="text-xs mt-1.5">In Twilio Console → WhatsApp sender → <span className="font-semibold">"When a message comes in"</span>, set the webhook URL to:</p>
                <code className="mt-2 block p-2 bg-background border border-border rounded font-mono text-xs break-all" data-testid="webhook-url">
                  {typeof window !== "undefined" ? `${window.location.origin}/api/twilio/whatsapp-webhook` : "/api/twilio/whatsapp-webhook"}
                </code>
              </div>
              <p className="mt-3 text-xs">Until Twilio is configured, every "send" is logged as <span className="font-mono">SIMULATED</span> and you can test inbound replies via the AI Agent's Simulate Reply box.</p>
            </div>
          </div>
        </div>

        <div className="border border-border bg-card rounded-md p-6">
          <h3 className="font-display text-xl font-semibold">AI Model</h3>
          <p className="text-sm text-muted-foreground mt-1">The brain behind every reminder script.</p>
          <div className="mt-5 flex items-center justify-between py-3 border-t border-border text-sm">
            <div className="text-muted-foreground">Model</div>
            <div className="font-mono">GPT 6 Sol (OpenAI)</div>
          </div>
          <div className="flex items-center justify-between py-3 border-t border-border text-sm">
            <div className="text-muted-foreground">Status</div>
            <Badge className="bg-success text-white">Active</Badge>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
